import React, { useEffect, useState } from "react";
import axiosInstance from "../axiosInstance";

function InfoItem({ label, value }) {
  return (
    <div className="p-3 rounded-3 border bg-light h-100">
      <small className="text-muted text-uppercase fw-bold" style={{ fontSize: 11 }}>
        {label}
      </small>
      <div className="fw-semibold mt-1">{value || "Not set"}</div>
    </div>
  );
}

function StudentViewModal({ student, onClose, onEdit }) {
  const [timetable, setTimetable] = useState([]);
  const [timetableLoading, setTimetableLoading] = useState(false);
  const [financeSummary, setFinanceSummary] = useState(null);
  const [financeLoading, setFinanceLoading] = useState(false);
  const [elearning, setElearning] = useState({ resources: [], live_classes: [] });
  const [elearningLoading, setElearningLoading] = useState(false);
  const [libraryBorrowings, setLibraryBorrowings] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);

  useEffect(() => {
    const loadTimetable = async () => {
      if (!student?.id) return;

      try {
        setTimetableLoading(true);
        const response = await axiosInstance.get(`students/${student.id}/timetable/`);
        setTimetable(response.data?.slots || []);
      } catch (error) {
        console.error(error);
        setTimetable([]);
      } finally {
        setTimetableLoading(false);
      }
    };

    loadTimetable();
  }, [student?.id]);

  useEffect(() => {
    const loadFinanceSummary = async () => {
      if (!student?.id) return;

      try {
        setFinanceLoading(true);
        const response = await axiosInstance.get(`fees/students/${student.id}/summary/`);
        setFinanceSummary(response.data || null);
      } catch (error) {
        console.error(error);
        setFinanceSummary(null);
      } finally {
        setFinanceLoading(false);
      }
    };

    loadFinanceSummary();
  }, [student?.id]);

  useEffect(() => {
    const loadElearning = async () => {
      if (!student?.id) return;

      try {
        setElearningLoading(true);
        const response = await axiosInstance.get(`elearning/students/${student.id}/resources/`);
        setElearning({
          resources: response.data?.resources || [],
          live_classes: response.data?.live_classes || [],
        });
      } catch (error) {
        console.error(error);
        setElearning({ resources: [], live_classes: [] });
      } finally {
        setElearningLoading(false);
      }
    };

    loadElearning();
  }, [student?.id]);

  useEffect(() => {
    const loadLibraryBorrowings = async () => {
      if (!student?.id) return;

      try {
        setLibraryLoading(true);
        const response = await axiosInstance.get(`library/users/${student.id}/borrowings/`);
        setLibraryBorrowings(response.data || []);
      } catch (error) {
        console.error(error);
        setLibraryBorrowings([]);
      } finally {
        setLibraryLoading(false);
      }
    };

    loadLibraryBorrowings();
  }, [student?.id]);

  if (!student) return null;

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      style={{ background: "rgba(0,0,0,0.45)" }}
    >
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content border-0 shadow">
          <div className="modal-header">
            <div className="d-flex align-items-center gap-3">
              <div
                className="rounded-circle text-white fw-bold d-flex align-items-center justify-content-center"
                style={{ width: 56, height: 56, background: "#125b2d" }}
              >
                {student.full_name
                  ?.split(" ")
                  .filter(Boolean)
                  .slice(0, 2)
                  .map((name) => name[0])
                  .join("") || "ST"}
              </div>
              <div>
                <h5 className="modal-title fw-bold">{student.full_name}</h5>
                <small className="text-muted">
                  {student.student_number} · {student.class_group_name || student.current_class || "No class"}
                </small>
              </div>
            </div>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>

          <div className="modal-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>
            <div className="d-flex flex-wrap gap-2 mb-3">
              <span className={`badge ${student.is_active ? "text-bg-success" : "text-bg-secondary"}`}>
                {student.is_active ? "ACTIVE" : "INACTIVE"}
              </span>
              {student.medical_conditions || student.allergies ? (
                <span className="badge text-bg-warning">Health notes</span>
              ) : (
                <span className="badge text-bg-light border">No health notes</span>
              )}
              {financeSummary?.summary && (
                <span className={`badge ${
                  financeSummary.summary.clearance_status === "PAID_IN_FULL"
                    ? "text-bg-success"
                    : financeSummary.summary.clearance_status === "PARTIALLY_PAID"
                    ? "text-bg-warning"
                    : "text-bg-danger"
                }`}>
                  {financeSummary.summary.clearance_status.replaceAll("_", " ")}
                </span>
              )}
            </div>

            <div className="row g-3">
              <div className="col-md-4">
                <InfoItem label="Student Number" value={student.student_number} />
              </div>
              <div className="col-md-4">
                <InfoItem label="Gender" value={student.gender} />
              </div>
              <div className="col-md-4">
                <InfoItem label="Date of Birth" value={student.date_of_birth} />
              </div>
              <div className="col-md-4">
                <InfoItem label="Class Group" value={student.class_group_name || student.current_class} />
              </div>
              <div className="col-md-4">
                <InfoItem label="Stream" value={student.stream} />
              </div>
              <div className="col-md-4">
                <InfoItem label="Academic Year" value={student.academic_year_name} />
              </div>
              <div className="col-md-4">
                <InfoItem label="Class Teacher" value={student.class_teacher_name} />
              </div>
              <div className="col-md-4">
                <InfoItem label="Enrollment Date" value={student.enrollment_date} />
              </div>
              <div className="col-md-6">
                <InfoItem label="Phone" value={student.phone} />
              </div>
              <div className="col-md-6">
                <InfoItem label="Email" value={student.email} />
              </div>
              <div className="col-md-6">
                <InfoItem label="Emergency Contact" value={student.emergency_contact_name} />
              </div>
              <div className="col-md-6">
                <InfoItem label="Emergency Phone" value={student.emergency_contact_phone} />
              </div>
              <div className="col-md-6">
                <InfoItem label="Medical Conditions" value={student.medical_conditions} />
              </div>
              <div className="col-md-6">
                <InfoItem label="Allergies" value={student.allergies} />
              </div>
              <div className="col-12">
                <InfoItem label="Address" value={student.address} />
              </div>
            </div>

            <div className="mt-4">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div>
                  <h6 className="fw-bold mb-0">Finance Status</h6>
                  <small className="text-muted">Admin view of student invoices, payments, and access guidance.</small>
                </div>
              </div>

              {financeLoading ? (
                <div className="alert alert-light border">Loading finance status...</div>
              ) : financeSummary ? (
                <>
                  <div className="row g-3 mb-3">
                    <FinanceBox label="Total Billed" value={financeSummary.summary.total_billed} />
                    <FinanceBox label="Total Paid" value={financeSummary.summary.total_paid} />
                    <FinanceBox label="Balance" value={financeSummary.summary.total_balance} />
                    <FinanceBox label="Status" value={financeSummary.summary.clearance_status.replaceAll("_", " ")} />
                  </div>

                  <div className="border rounded-3 p-3 mb-3">
                    <div className="fw-bold mb-2">Feature Access Guidance</div>
                    <div className="d-flex flex-wrap gap-2">
                      {Object.entries(financeSummary.allowed_features || {}).map(([feature, allowed]) => (
                        <span key={feature} className={`badge ${allowed ? "text-bg-success" : "text-bg-secondary"}`}>
                          {feature.replaceAll("_", " ")}: {allowed ? "Allowed" : "Restricted"}
                        </span>
                      ))}
                    </div>
                    <small className="text-muted d-block mt-2">
                      This is an admin finance flag. We can enforce these restrictions in student/parent dashboards when those dashboards are built.
                    </small>
                  </div>

                  <div className="table-responsive border rounded-3">
                    <table className="table table-sm align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Invoice</th>
                          <th>Total</th>
                          <th>Paid</th>
                          <th>Balance</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {financeSummary.invoices?.length ? (
                          financeSummary.invoices.map((invoice) => (
                            <tr key={invoice.id}>
                              <td>{invoice.invoice_number}</td>
                              <td>{invoice.currency_code} {invoice.total_amount}</td>
                              <td>{invoice.currency_code} {invoice.amount_paid}</td>
                              <td className="fw-bold">{invoice.currency_code} {invoice.balance}</td>
                              <td>{invoice.status}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" className="text-center text-muted py-3">
                              No invoices found for this student.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="alert alert-light border">Finance status is not available yet.</div>
              )}
            </div>

            <div className="mt-4">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div>
                  <h6 className="fw-bold mb-0">Student Timetable</h6>
                  <small className="text-muted">Slots are loaded from the student's current class group.</small>
                </div>
                <span className="badge text-bg-light border">
                  {timetable.length} slot{timetable.length === 1 ? "" : "s"}
                </span>
              </div>

              <div className="table-responsive border rounded-3">
                <table className="table table-sm align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Day</th>
                      <th>Period</th>
                      <th>Subject</th>
                      <th>Teacher</th>
                      <th>Room</th>
                    </tr>
                  </thead>
                  <tbody>
                    {timetableLoading ? (
                      <tr>
                        <td colSpan="5" className="text-center text-muted py-3">
                          Loading timetable...
                        </td>
                      </tr>
                    ) : timetable.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center text-muted py-3">
                          No timetable slots found for this student.
                        </td>
                      </tr>
                    ) : (
                      timetable.map((slot) => (
                        <tr key={slot.id}>
                          <td>{slot.day_name}</td>
                          <td>
                            <div className="fw-semibold">{slot.period_name}</div>
                            <small className="text-muted">{slot.period_time}</small>
                          </td>
                          <td>{slot.subject_name || "Break / Lunch"}</td>
                          <td>{slot.teacher_name || "Not assigned"}</td>
                          <td>{slot.room || "-"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-4">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div>
                  <h6 className="fw-bold mb-0">E-learning</h6>
                  <small className="text-muted">Published materials and live lessons for this student's class audience.</small>
                </div>
                <div className="d-flex gap-2">
                  <span className="badge text-bg-light border">
                    {elearning.resources.length} material{elearning.resources.length === 1 ? "" : "s"}
                  </span>
                  <span className="badge text-bg-light border">
                    {elearning.live_classes.length} live class{elearning.live_classes.length === 1 ? "" : "es"}
                  </span>
                </div>
              </div>

              {elearningLoading ? (
                <div className="alert alert-light border">Loading e-learning...</div>
              ) : (
                <div className="row g-3">
                  <div className="col-lg-6">
                    <div className="border rounded-3 h-100">
                      <div className="p-3 border-bottom bg-light fw-bold">Learning Materials</div>
                      <div className="table-responsive">
                        <table className="table table-sm align-middle mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Title</th>
                              <th>Subject</th>
                              <th>Access</th>
                              <th>Open</th>
                            </tr>
                          </thead>
                          <tbody>
                            {elearning.resources.length ? elearning.resources.map((resource) => (
                              <tr key={resource.id}>
                                <td>
                                  <div className="fw-semibold">{resource.title}</div>
                                  <small className="text-muted">{resource.resource_type}</small>
                                </td>
                                <td>{resource.subject_name || "-"}</td>
                                <td><AccessBadge status={resource.access_status} /></td>
                                <td>
                                  {resource.file_url ? (
                                    <a href={resource.file_url} target="_blank" rel="noreferrer">File</a>
                                  ) : resource.external_url ? (
                                    <a href={resource.external_url} target="_blank" rel="noreferrer">Link</a>
                                  ) : "-"}
                                </td>
                              </tr>
                            )) : (
                              <tr>
                                <td colSpan="4" className="text-center text-muted py-3">
                                  No e-learning materials found.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  <div className="col-lg-6">
                    <div className="border rounded-3 h-100">
                      <div className="p-3 border-bottom bg-light fw-bold">Live Online Classes</div>
                      <div className="table-responsive">
                        <table className="table table-sm align-middle mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Title</th>
                              <th>When</th>
                              <th>Access</th>
                              <th>Join</th>
                            </tr>
                          </thead>
                          <tbody>
                            {elearning.live_classes.length ? elearning.live_classes.map((liveClass) => (
                              <tr key={liveClass.id}>
                                <td>
                                  <div className="fw-semibold">{liveClass.title}</div>
                                  <small className="text-muted">{liveClass.platform} · {liveClass.subject_name || "-"}</small>
                                </td>
                                <td>{formatDateTime(liveClass.scheduled_start)}</td>
                                <td><AccessBadge status={liveClass.access_status} /></td>
                                <td>
                                  {liveClass.meeting_url ? (
                                    <a href={liveClass.meeting_url} target="_blank" rel="noreferrer">Join</a>
                                  ) : "-"}
                                </td>
                              </tr>
                            )) : (
                              <tr>
                                <td colSpan="4" className="text-center text-muted py-3">
                                  No upcoming live classes found.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-4">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div>
                  <h6 className="fw-bold mb-0">Library History</h6>
                  <small className="text-muted">Borrowed books and digital library resources for this student.</small>
                </div>
                <span className="badge text-bg-light border">
                  {libraryBorrowings.length} record{libraryBorrowings.length === 1 ? "" : "s"}
                </span>
              </div>

              <div className="table-responsive border rounded-3">
                <table className="table table-sm align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Item</th>
                      <th>Borrowed</th>
                      <th>Due</th>
                      <th>Returned</th>
                      <th>Fine</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {libraryLoading ? (
                      <tr>
                        <td colSpan="6" className="text-center text-muted py-3">
                          Loading library history...
                        </td>
                      </tr>
                    ) : libraryBorrowings.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center text-muted py-3">
                          No library records found for this student.
                        </td>
                      </tr>
                    ) : (
                      libraryBorrowings.map((borrowing) => (
                        <tr key={borrowing.id}>
                          <td>
                            <div className="fw-semibold">{borrowing.item_title}</div>
                            <small className="text-muted">{borrowing.item_author || borrowing.item_type || "-"}</small>
                          </td>
                          <td>{borrowing.borrowed_date || "-"}</td>
                          <td>{borrowing.due_date || "-"}</td>
                          <td>{borrowing.returned_date || "-"}</td>
                          <td>{borrowing.fine_amount || 0}</td>
                          <td><LibraryStatus value={borrowing.is_overdue ? "OVERDUE" : borrowing.status} /></td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button className="btn btn-light" onClick={onClose}>
              Close
            </button>
            <button className="btn btn-success" onClick={() => onEdit(student)}>
              <i className="bi bi-pencil-square me-2"></i>
              Edit Student
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function FinanceBox({ label, value }) {
  return (
    <div className="col-md-3">
      <div className="border rounded-3 p-3 bg-light h-100">
        <small className="text-muted fw-bold text-uppercase" style={{ fontSize: 11 }}>
          {label}
        </small>
        <div className="fw-bold mt-1">{value || 0}</div>
      </div>
    </div>
  );
}

function AccessBadge({ status }) {
  if (!status) return <span className="badge text-bg-light border">Unknown</span>;
  if (status.can_view) {
    return <span className="badge text-bg-success">Allowed</span>;
  }
  return <span className="badge text-bg-danger">Restricted</span>;
}

function LibraryStatus({ value }) {
  const normalized = String(value || "").toUpperCase();
  const className = ["BORROWED", "RETURNED"].includes(normalized)
    ? "text-bg-success"
    : ["OVERDUE", "DAMAGED"].includes(normalized)
      ? "text-bg-warning"
      : normalized === "LOST"
        ? "text-bg-danger"
        : "text-bg-secondary";
  return <span className={`badge ${className}`}>{String(value || "-").replaceAll("_", " ")}</span>;
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

export default StudentViewModal;
